import { getVariablePreviewValue } from "@/oxygen/variables/lib/utils";
import { globalStore } from "@/store/store";
import {
  Completion,
  CompletionContext,
  CompletionResult
} from "@codemirror/autocomplete";
import { cssLanguage } from "@codemirror/lang-css";
import { syntaxTree } from "@codemirror/language";
import { EditorView } from "@codemirror/view";
import { getGlobalAndDefaultColors } from "@/lib/colors/colors";
import { Variable } from "@/oxygen/variables/lib/types";
import { SyntaxNode } from "@lezer/common";

function formatVariableValue(variable: Variable) {
  const value = getVariablePreviewValue(variable);

  if (variable.type === "image_url" && typeof value === "string") {
    return value.slice(0, 10) + "...";
  }

  return value;
}

function getBreakdanceVariables() {
  return getGlobalAndDefaultColors().map(color => {
    return {
      label: color.label,
      displayLabel: color.label,
      cssVariableName: color.cssVariableName,
      detail: "",
      collection: "Global Colors"
    };
  });
}

function getOxyVariables() {
  return globalStore.variables.map(variable => {
    return {
      label: variable.label,
      displayLabel: variable.cssVariableName,
      cssVariableName: variable.cssVariableName,
      detail: `${formatVariableValue(variable)}`,
      collection: variable.collection
    };
  });
}

function filterVariables(searchText: string) {
  const oxyVariables = getOxyVariables();
  const bdeVariables = getBreakdanceVariables();

  const variables = [...oxyVariables, ...bdeVariables];

  return variables.filter(variable => {
    return (
      variable.cssVariableName.includes(searchText) ||
      variable.label.toLowerCase().includes(searchText.toLowerCase())
    );
  });
}

// If the cursor is inside a var() function, it will append the variable name inside the var() function.
// If the cursor is not inside a var() function, it will auto wrap the variable name with var().
const applyFunction = (
  view: EditorView,
  completion: Completion,
  from: number,
  to: number
) => {
  const variableName = completion.label;
  const isInsideVar = view.state.sliceDoc(0, from).match(/var\(--[^)]*$/);

  if (isInsideVar) {
    // If already inside var(), just insert the variable name
    view.dispatch({
      changes: {
        from,
        to,
        insert: variableName
      },
      selection: {
        anchor: to + variableName.length,
        head: to + variableName.length
      }
    });
  } else {
    // If not inside var(), wrap with var()
    view.dispatch({
      changes: {
        from: from - 2,
        to,
        insert: `var(--${variableName})`
      }
    });
  }
};

export function isVariableName(node: SyntaxNode) {
  return node.name === "VariableName";
}

export function isParserInUnknownState(node: SyntaxNode) {
  // There is a bug in CodeMirror that misidentifies values without colons as PseudoClassName or TagName.
  return node.name === "PseudoClassName" || node.name === "TagName";
}

export function isInDeclaration(
  nodeBefore: SyntaxNode,
  context: CompletionContext
) {
  const above = nodeBefore.resolve(context.pos);
  const before = above?.childBefore(context.pos);

  return before?.name === "VariableName" && above.name === "Declaration";
}

export function isCursorInVariableContext(context: CompletionContext) {
  const searchStart = Math.max(0, context.pos - 100);
  const textBeforeCursor = context.state.sliceDoc(searchStart, context.pos);
  const isInsideVar = textBeforeCursor.match(/var\(--[^)]*$/);
  const isInsideCustomProperty = textBeforeCursor.match(/--[^:;]*$/);

  // Only proceed if we're actually in a var() function or -- custom property
  return isInsideVar || isInsideCustomProperty;
}

function completeCssVariables(
  context: CompletionContext
): CompletionResult | null {
  const nodeBefore = syntaxTree(context.state).resolveInner(context.pos, -1);
  const isParserBroken = isParserInUnknownState(nodeBefore);
  const shouldAutoComplete = isVariableName(nodeBefore) || isParserBroken;

  const applyFn = isInDeclaration(nodeBefore, context)
    ? undefined
    : applyFunction;

  if (isParserBroken && !isCursorInVariableContext(context)) {
    return null;
  }

  if (shouldAutoComplete) {
    const searchText = context.state.sliceDoc(nodeBefore.from + 2, context.pos);
    const variables = filterVariables(searchText);

    if (variables.length === 0) return null;

    const options: Completion[] = variables.map(variable => ({
      label: variable.cssVariableName,
      displayLabel: variable.displayLabel,
      detail: variable.detail,
      section: {
        name: variable.collection,
        rank: 100 // Force variables to be the first in the list
      },
      type: "variable",
      boost: 99, // Force variables to be the first in the list
      apply: applyFn
    }));

    return {
      from: nodeBefore.from + 2,
      options
    };
  }

  return null;
}

export const cssVariablesCompletions = cssLanguage.data.of({
  autocomplete: completeCssVariables
});
